import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon, User, Search, LogOut, KeyRound } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import NotificationBell from '../NotificationBell';
import GlobalSearch from '../GlobalSearch';
import ChangePasswordDialog from '../ChangePasswordDialog';

const SUUBI_LOGO = "https://customer-assets.emergentagent.com/job_community-school-hub/artifacts/vxd2g9ou_suubi%20final_Logo_00_00.jpg";

const Header = ({ title }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  // Cmd/Ctrl+K shortcut for global search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const getInitials = (name) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <>
      <header className="h-16 bg-card border-b border-border px-6 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img src={SUUBI_LOGO} alt="Suubi" className="h-8 w-8 rounded-full object-cover lg:hidden" />
          <h1 className="font-heading font-semibold text-xl text-foreground">{title}</h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Global Search trigger */}
          <button
            onClick={() => setSearchOpen(true)}
            className="hidden md:flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 hover:bg-muted/70 px-3 py-1.5 rounded-md border transition-colors"
            data-testid="global-search-trigger"
          >
            <Search className="h-4 w-4" />
            <span>Search…</span>
            <kbd className="text-[10px] bg-background border rounded px-1 py-0.5 ml-2">⌘ K</kbd>
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSearchOpen(true)}
            className="md:hidden"
            data-testid="global-search-mobile-trigger"
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="text-muted-foreground hover:text-foreground"
          >
            {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>

          {/* Notifications */}
          <NotificationBell />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:block text-sm font-medium">{user?.full_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>{user?.full_name}</div>
                <div className="text-xs text-muted-foreground font-normal capitalize">
                  {user?.role?.replace(/_/g, ' ')}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePwOpen(true)} data-testid="menu-change-password">
                <KeyRound className="mr-2 h-4 w-4" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open('/admin/audit', '_blank')} className="md:hidden">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </>
  );
};

export default Header;
